import { BaseAPI } from "./BaseAPI";

import type { APIConf } from "./BaseAPI";
import type {
  CreateCastingParams,
  CreateCastingTranslationParams,
  CreateCastingTranslationResponse,
  CreateTranslationParams,
  CreateTranslationResponse,
  DeleteMediaPictureTagResponse,
  DeletePictureParams,
  GetCastingListParams,
  GetCastingListResponse,
  GetCastingResponse,
  GetFastIndexMediaListParams,
  GetFastIndexMediaListResponse,
  GetFreeMediaListParams,
  GetFreeMediaListResponse,
  GetIndexMediaAvailableListResponse,
  GetMediaFastAssetsPreviewResponse,
  GetMediaFastAssetsResponse,
  GetMediaListParams,
  GetMediaListResponse,
  GetMediaResponse,
  GetMediasMetadataListResponse,
  GetMediasResponse,
  GetMediaStatusParams,
  GetMediaStatusResponse,
  GetMediasWithTrailersListResponse,
  UpdateMediaParams,
  UpdateMediaPictureTagParams,
  UpdateMediaPictureTagResponse,
  UpdateMediaResponse,
  UploadPictureParams,
  UploadPictureResponse,
  ValidateEpgResponse,
} from "./MediaAPI.types";
import type HostedMedia from "../models/HostedMedia";

class MediaAPIClass extends BaseAPI {
  constructor(conf: APIConf) {
    super(conf);
    this.service = "media";
    this.version = "v6";
  }

  async getMediaList({
    page = 1,
    perPage = 10,
    filter,
    order,
    search,
  }: GetMediaListParams): Promise<GetMediaListResponse> {
    return this.axios
      .post<GetMediaListResponse>(`${this.routeBase}/admin/index_medias`, {
        filter,
        order,
        page,
        per_page: perPage,
        search: search ?? null,
      })
      .then((response) => response.data);
  }

  async getIndexMediaAvailableList(params: {
    type: string;
    external_type?: "stream";
    live_status?: string[];
  }): Promise<GetIndexMediaAvailableListResponse> {
    const filter = {
      ...params,
      status: "enabled",
    };
    return this.axios
      .post<GetIndexMediaAvailableListResponse>(
        `${this.routeBase}/admin/index_medias`,
        {
          filter,
        },
      )
      .then((response) => response.data);
  }

  async getChildClassificationList(): Promise<unknown> {
    return this.axios
      .get(`${this.routeBase}/admin/medias/child/classifications`)
      .then((response) => response.data);
  }

  async createChildMedia({
    masterId,
    newMedia,
  }: {
    masterId: string;
    newMedia: HostedMedia;
  }): Promise<unknown> {
    return this.axios
      .post(`${this.routeBase}/admin/medias/${masterId}/child`, newMedia)
      .then((response) => response.data);
  }

  async deleteChildMedia(childId: string): Promise<unknown> {
    return this.axios
      .delete(`${this.routeBase}/admin/medias/child/${childId}`)
      .then((response) => response.data);
  }

  async createTranslation({
    mediaId,
    languageId,
  }: CreateTranslationParams): Promise<CreateTranslationResponse> {
    return this.axios
      .post<CreateTranslationResponse>(
        `${this.routeBase}/admin/medias/${mediaId}/translation`,
        {
          language_uuid: languageId,
        },
      )
      .then((response) => response.data);
  }

  async getMediasMetadataList(): Promise<GetMediasMetadataListResponse> {
    return this.axios
      .get<GetMediasMetadataListResponse>(
        `${this.routeBase}/admin/medias/metadata`,
      )
      .then((response) => response.data);
  }

  async getIndexMetadatasMediaList(): Promise<unknown> {
    return this.axios
      .get(`${this.routeBase}/admin/medias/index_metadatas`)
      .then((response) => response.data);
  }

  async copyMetadataMedia({
    fromUUID,
    toUUID,
  }: {
    fromUUID: string;
    toUUID: string;
  }): Promise<unknown> {
    return this.axios
      .post(`${this.routeBase}/admin/medias/copy_metadata`, {
        from_uuid: fromUUID,
        to_uuid: toUUID,
      })
      .then((response) => response.data);
  }

  async getFreeMediaList({
    page = 1,
    perPage = 100,
    search,
    order = { field: "label", direction: "up" },
  }: GetFreeMediaListParams = {}): Promise<GetFreeMediaListResponse> {
    return this.getMediaList({
      page,
      perPage,
      order,
      filter: { free: true },
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      search: search || null,
    });
  }

  async getFastIndexMediaList(
    params: GetFastIndexMediaListParams,
  ): Promise<GetFastIndexMediaListResponse> {
    const { channelId } = params;

    const order = {
      direction: params.order?.direction ?? "down",
      field: params.order?.field ?? "updated_at",
    };
    const page = params.page ?? 1;
    const perPage = params.perPage ?? 10;
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const search = params.search || null;
    const filter = params.filter ?? null;

    const data = {
      order,
      page,
      per_page: perPage,
      search,
      filter,
    };

    return this.axios
      .post<GetFastIndexMediaListResponse>(
        `${this.routeBase}/admin/fast/${channelId}/index_medias`,
        data,
      )
      .then((response) => response.data);
  }

  async getMedia(uuid: string): Promise<GetMediaResponse> {
    return this.axios
      .get<GetMediaResponse>(`${this.routeBase}/admin/medias/${uuid}`)
      .then((response) => response.data);
  }

  async getMediasWithTrailersList(): Promise<GetMediasWithTrailersListResponse> {
    return this.axios
      .get<GetMediasWithTrailersListResponse>(
        `${this.routeBase}/admin/medias/medias_trailers`,
      )
      .then((response) => response.data);
  }

  async deleteCasting(uuid: string): Promise<void> {
    await this.axios.delete(`${this.apiBase}/media/v3/admin/castings/${uuid}`);
  }

  async getCastingList({
    page = 1,
    perPage = 10,
    search,
    order,
  }: GetCastingListParams = {}): Promise<GetCastingListResponse> {
    return this.axios
      .get<GetCastingListResponse>(`${this.routeBase}/admin/castings`, {
        params: {
          page,
          per_page: perPage,
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          search: search || null,
          ...(order ? { column: order.field, direction: order.direction } : {}),
        },
      })
      .then((response) => response.data);
  }

  async getCastings(
    params: { page?: number; perPage?: number | string } = {},
  ): Promise<GetCastingListResponse> {
    const page = params.page ?? 1;
    const perPage = params.perPage ?? 10;

    return this.axios
      .get<GetCastingListResponse>(`${this.routeBase}/admin/castings`, {
        params: { per_page: perPage, page },
      })
      .then((response) => response.data);
  }

  async getCasting(uuid: string): Promise<GetCastingResponse> {
    return this.axios
      .get<GetCastingResponse>(
        `${this.apiBase}/${this.service}/v5/admin/castings/${uuid}`,
      )
      .then((response) => response.data);
  }

  async createCastingTranslation({
    castingId,
    languageUuid,
  }: CreateCastingTranslationParams): Promise<CreateCastingTranslationResponse> {
    return this.axios
      .post<CreateCastingTranslationResponse>(
        `${this.routeBase}/admin/castings/${castingId}/translation`,
        {
          language_uuid: languageUuid,
        },
      )
      .then((response) => response.data);
  }

  async updateCasting({
    uuid,
    data,
  }: CreateCastingParams): Promise<GetCastingResponse> {
    return this.axios
      .post<GetCastingResponse>(
        `${this.apiBase}/${this.service}/v3/admin/castings/${uuid}`,
        data,
      )
      .then((response) => response.data);
  }

  async getMedias(): Promise<GetMediasResponse> {
    return this.axios
      .get<GetMediasResponse>(`${this.routeBase}/admin/medias`)
      .then((response) => response.data);
  }

  async uploadPicture({
    itemId,
    pictureFile,
    pictureFormat,
    translationId,
    contentType = "medias",
  }: UploadPictureParams): Promise<UploadPictureResponse> {
    const formData = new FormData();
    formData.append("picture", pictureFile);

    return this.axios
      .post<UploadPictureResponse>(
        `${this.routeBase}/admin/${contentType}/${itemId}/picture`,
        formData,
        {
          params: {
            translation_uuid: translationId,
            picture_field: pictureFormat,
          },
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      )
      .then((response) => response.data);
  }

  async deletePicture({
    itemId,
    translationId,
    contentType = "medias",
  }: DeletePictureParams): Promise<void> {
    await this.axios.delete(
      `${this.routeBase}/admin/${contentType}/${itemId}/picture`,
      {
        params: {
          translation_uuid: translationId,
        },
      },
    );
  }

  async getMediaFastAssets(uuid: string): Promise<GetMediaFastAssetsResponse> {
    return this.axios
      .get<GetMediaFastAssetsResponse>(
        `${this.routeBase}/admin/medias/${uuid}/fast_assets`,
      )
      .then((response) => response.data);
  }

  async deletePictureTag(
    tagId: string,
  ): Promise<DeleteMediaPictureTagResponse> {
    return this.axios
      .delete<DeleteMediaPictureTagResponse>(
        `${this.routeBase}/admin/medias/tags/${tagId}`,
      )
      .then((response) => response.data);
  }

  async updatePictureTag({
    tagId,
    tagValue,
    translationId,
    ratioId,
    itemType,
  }: UpdateMediaPictureTagParams): Promise<UpdateMediaPictureTagResponse> {
    return this.axios
      .put<UpdateMediaPictureTagResponse>(
        `${this.routeBase}/admin/${itemType}/tags/${tagId}`,
        {
          picture_field: ratioId,
          translation_uuid: translationId,
          label: tagValue,
        },
      )
      .then((response) => response.data);
  }

  async updateMedia({
    data,
    uuid,
  }: UpdateMediaParams): Promise<UpdateMediaResponse> {
    return this.axios
      .put<UpdateMediaResponse>(`${this.routeBase}/admin/medias/${uuid}`, data)
      .then((response) => response.data);
  }

  async getMediaStatus({
    uuid,
    projectSlug,
  }: GetMediaStatusParams): Promise<GetMediaStatusResponse> {
    return this.axios
      .get<GetMediaStatusResponse>(
        `${this.routeBase}/admin/medias/${uuid}/status`,
        {
          headers: { "xx-ok-project": projectSlug },
        },
      )
      .then((response) => response.data);
  }

  async getMediaFastAssetsPreview(
    uuid: string,
  ): Promise<GetMediaFastAssetsPreviewResponse> {
    return this.axios
      .get<GetMediaFastAssetsPreviewResponse>(
        `${this.routeBase}/admin/medias/${uuid}/fast_assets/preview`,
      )
      .then((response) => response.data);
  }

  async validateEpg(epgUrl: string): Promise<ValidateEpgResponse> {
    return this.axios
      .post<ValidateEpgResponse>(`${this.routeBase}/admin/epg/validate`, {
        epg_url: epgUrl,
      })
      .then((response) => response.data);
  }
}

const MediaAPI = new MediaAPIClass({
  token: localStorage.token,
});

export default MediaAPI;
